import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";
import BatchStatusIcon from "./BatchStatusIcon";
import TabRemoveButton from "./TabRemoveButton";
import { BatchStatus, isDoneStatus } from "./useBatchSession";

/** A video tab's own status: an error or unsaved platform wins, then "all saved". */
function videoStatus(items, statusOf) {
  const statuses = items.map(statusOf);
  if (statuses.includes(BatchStatus.ERROR)) return BatchStatus.ERROR;
  if (statuses.includes(BatchStatus.UNSAVED) || statuses.includes(BatchStatus.OUTDATED)) return BatchStatus.UNSAVED;
  if (statuses.every(isDoneStatus)) return BatchStatus.SAVED;
  if (statuses.includes(BatchStatus.APPLIED)) return BatchStatus.APPLIED;
  return BatchStatus.IDLE;
}

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Sheet-style video tabs along the bottom, grouped by brand when there is more
 * than one. One tablist with a roving tab stop: arrows/Home/End move focus,
 * Enter or Space switches. The brand labels are visual only; each tab's
 * accessible name carries its brand instead.
 */
export default function VideoTabBar({ groups, activeVideoKey, statusOf, onSelect, onRemove, sx }) {
  const showBrands = groups.length > 1;
  const scrollerRef = useRef(null);
  const tabRefs = useRef({});
  const [edges, setEdges] = useState({ left: false, right: false });
  const order = groups.flatMap((g) => g.videos.map((v) => v.key));

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
  }, []);

  useLayoutEffect(updateEdges, [updateEdges, groups]);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateEdges]);

  // Keep the selected tab in view.
  useEffect(() => {
    tabRefs.current[activeVideoKey]?.scrollIntoView?.({
      behavior: reducedMotion() ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeVideoKey]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: reducedMotion() ? "auto" : "smooth" });
  };

  const handleKeyDown = (e, key) => {
    // No Delete/Backspace shortcut: focus stays on the tab while the editor
    // is used, so a Delete meant for the canvas removed the video. The × does.
    const i = order.indexOf(key);
    let next = null;
    if (e.key === "ArrowRight") next = order[Math.min(order.length - 1, i + 1)];
    else if (e.key === "ArrowLeft") next = order[Math.max(0, i - 1)];
    else if (e.key === "Home") next = order[0];
    else if (e.key === "End") next = order[order.length - 1];
    if (!next) return;
    e.preventDefault();
    tabRefs.current[next]?.focus();
  };

  const arrowSx = { width: 28, height: 28, color: "#475569", flexShrink: 0, "&.Mui-disabled": { opacity: 0.35 } };

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", bgcolor: "#f1f5f9", borderTop: "1px solid #e2e8f0", flexShrink: 0, ...sx }}>
      <IconButton aria-label="Scroll videos left" onClick={() => scrollBy(-1)} disabled={!edges.left} sx={{ ...arrowSx, alignSelf: "center", mx: 0.25 }}>
        <ChevronLeftIcon />
      </IconButton>

      <Box
        ref={scrollerRef}
        role="tablist"
        aria-label="Videos"
        onScroll={updateEdges}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "stretch",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {groups.map((group, gi) => (
          <Box
            key={group.brandName}
            role="presentation"
            sx={{ display: "flex", alignItems: "stretch", flexShrink: 0, borderLeft: gi > 0 ? "2px solid #cbd5e1" : "none", pl: gi > 0 ? 0.5 : 0 }}
          >
            {showBrands && (
              <Box aria-hidden display="flex" alignItems="center" gap={0.5} px={1.25} sx={{ color: "#4338ca", flexShrink: 0 }}>
                <BusinessIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" fontWeight={700} noWrap sx={{ textTransform: "uppercase", letterSpacing: 0.5, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {group.brandName}
                </Typography>
              </Box>
            )}
            {group.videos.map((v) => {
              const selected = v.key === activeVideoKey;
              const saved = v.items.filter((i) => isDoneStatus(statusOf(i))).length;
              const title = v.video.title || v.video.video_title || "";
              return (
                <Tooltip key={v.key} title={title} placement="top" enterDelay={400} disableInteractive>
                  <ButtonBase
                    ref={(el) => {
                      tabRefs.current[v.key] = el;
                    }}
                    role="tab"
                    aria-selected={selected}
                    tabIndex={selected ? 0 : -1}
                    aria-label={`${showBrands ? `${group.brandName}, ` : ""}${v.video.unique_key}${title ? `, ${title}` : ""}, ${saved} of ${v.items.length} platforms saved`}
                    onClick={() => onSelect(v.key)}
                    onKeyDown={(e) => handleKeyDown(e, v.key)}
                    sx={{
                      minHeight: 26,
                      px: 0.875,
                      gap: 0.5,
                      flexShrink: 0,
                      borderTop: "2px solid",
                      borderTopColor: selected ? "#4338ca" : "transparent",
                      borderRight: "1px solid #e2e8f0",
                      bgcolor: selected ? "white" : "transparent",
                      color: selected ? "#1e1b4b" : "#475569",
                      transition: "background-color 150ms ease-out",
                      "&:hover": { bgcolor: selected ? "white" : "#e2e8f0" },
                      "&.Mui-focusVisible": { outline: "2px solid #4338ca", outlineOffset: -2 },
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    }}
                  >
                    <BatchStatusIcon status={videoStatus(v.items, statusOf)} size={12} />
                    <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 11, fontWeight: selected ? 700 : 600 }}>
                      {v.video.unique_key}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: 10 }}>
                      {saved}/{v.items.length}
                    </Typography>
                    <TabRemoveButton label={`Remove ${v.video.unique_key} from the batch editor`} onRemove={() => onRemove(v.key)} size={13} />
                  </ButtonBase>
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>

      <IconButton aria-label="Scroll videos right" onClick={() => scrollBy(1)} disabled={!edges.right} sx={{ ...arrowSx, alignSelf: "center", mx: 0.25 }}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}
